/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { globalAlert, globalLoader } from "../../actions/commonActions";
import ConfirmationModal from "../_common/confirmModal";
import { LmsSectionEditor } from "./lmsSectionsPanel";
import LmsAssetList from "./lmsAssetList";
import LmsAudienceAssets from "./lmsAudienceAssets";
import { assetPayload, assetIdOf, assetIcon } from "../../admin/_lmsHelpers";
import {
  SERVICE_AREAS,
  lmsReorderModulesApi,
  lmsDeleteModuleApi,
  isEndpointMissing,
  lmsSaveModuleApi,
  lmsAddModuleIntroAssetApi,
  lmsUpdateModuleIntroAssetApi,
  lmsDeleteModuleIntroAssetApi,
  lmsAddCompletionAssetApi,
  lmsUpdateCompletionAssetApi,
  lmsDeleteCompletionAssetApi,
  lmsAddSupportAssetApi,
  lmsDeleteSupportAssetApi,
  lmsAddSectionApi,
  lmsDeleteSectionApi,
} from "../../admin/_lmsActions";

const sortByOrder = (arr = []) =>
  [...arr].sort((a, b) => (a?.order || 0) - (b?.order || 0));
const sid = (s) => s?._id || s?.sectionId;
const mid = (m) => m?._id || m?.moduleId;
const elIcon = (type) =>
  type === "quiz" ? "fas fa-clipboard-question" : assetIcon(type);
const assetIsFilled = (asset) =>
  !!(asset?.url || asset?.content || asset?.signedUrl || asset?.fileUrl);
const wrappedAssetIsFilled = (item) => assetIsFilled(item?.asset || item);
const hasAssetType = (assets = [], type) =>
  assets.some((asset) => asset?.type === type && assetIsFilled(asset));
const hasIntroVideo = (item) => hasAssetType(item?.introductionAssets || [], "video");
const hasCompletionPdf = (item) =>
  (item?.completionAssets || []).some(
    (asset) =>
      (asset?.asset || asset)?.type === "pdf" && wrappedAssetIsFilled(asset)
  );
const hasAnySupportPdf = (item) =>
  (item?.supportServiceAssets || item?.supportServicesAssets || []).some(
    (asset) =>
      (asset?.asset || asset)?.type === "pdf" && wrappedAssetIsFilled(asset)
  );
const sectionHasVideo = (section) => hasAssetType(section?.assets || [], "video");
const sectionHasSummary = (section) => hasAssetType(section?.assets || [], "pdf");
// const sectionHasQuiz = (section) => !!(section?.quizDoc || section?.quiz);
const sectionHasQuiz = (section) => {
  // If the full quiz document is available, determine readiness
  // by whether it actually contains questions.
  if (section?.quizDoc) {
    return (section.quizDoc.questions?.length ?? 0) > 0;
  }

  // Fallback: if only a quiz id is present (e.g. older data),
  // treat it as having a quiz.
  return !!section?.quiz;
};

const sectionIsReady = (section) =>
  sectionHasVideo(section) && sectionHasQuiz(section);
const moduleIsReady = (item) => {
  const sections = item?.sections || [];
  return (
    hasIntroVideo(item) &&
    sections.length > 0 &&
    sections.every(sectionIsReady)
  );
};
const statusClass = (ready) => (ready ? "is-ok" : "is-warning");
const moduleIntroUploadTitle = (course, fallback) => {
  const courseName = String(course?.title || course?.slug || fallback || "course").trim();
  return `${courseName}-module-introduction`;
};

// ─────────────────────────────────────────────────────────────────────────────
// The full Learning-content editor: a single left column that carries BOTH the
// module list (add / drag-reorder / select) AND the selected module's element
// sequence tree (Introduction → Parts → Summary → Support), plus a right column
// with the contextual editor for the selected node. Delete-module lives in the
// content header. Reuses LmsSectionEditor / asset lists. Consumed by
// lmsLearningContentStep (wizard + workspace).
// ─────────────────────────────────────────────────────────────────────────────
const LmsModuleWorkspace = ({
  courseId,
  course,
  modules = [],
  onRefresh,
  registerUnsavedChangesGuard,
  setWizardDirty,
}) => {
  const { t } = useTranslation();

  const sortedModules = useMemo(() => sortByOrder(modules), [modules]);

  const [selectedModuleId, setSelectedModuleId] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [toDeleteModule, setToDeleteModule] = useState(null);
  const [addModuleOpen, setAddModuleOpen] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [moduleTitleDraft, setModuleTitleDraft] = useState("");

  const [selected, setSelected] = useState("intro"); // node key within module
  const [expanded, setExpanded] = useState({ intro: true });
  const [confirmSection, setConfirmSection] = useState(null);
  const [toDeleteKeyTips, setToDeleteKeyTips] = useState(null);
  const [toDeleteAsset, setToDeleteAsset] = useState(null);
  const [toDeleteSupportAsset, setToDeleteSupportAsset] = useState(null);
  const quizNavigationGuardRef = useRef(null);


  // Keep a valid module selected as the list changes.
  useEffect(() => {
    if (selectedModuleId && sortedModules.some((m) => mid(m) === selectedModuleId))
      return;
    setSelectedModuleId(sortedModules.length ? mid(sortedModules[0]) : null);
  }, [modules]);

  const module =
    selectedModuleId
      ? sortedModules.find((m) => mid(m) === selectedModuleId)
      : null;
  const moduleId = mid(module);
  const moduleUploadContext = {
    courseId,
    courseSlug: course?.slug,
    courseTitle: course?.title,
    moduleId,
    moduleTitle: module?.title,
  };
  const noModules = sortedModules.length === 0;

  const sections = useMemo(() => sortByOrder(module?.sections || []), [module]);
  const intro = useMemo(() => sortByOrder(module?.introductionAssets), [module]);
  const completion = useMemo(() => sortByOrder(module?.completionAssets), [module]);
  const support = module?.supportServiceAssets || module?.supportServicesAssets || [];

  // A Part's sub-nodes are keyed `sec-<id>#video` and `#quiz`;
  // selecting one expands its parent Part group.
  const parentOf = (key) => (key.includes("#") ? key.split("#")[0] : key);
  const runWithQuizGuard = useCallback((action) => {
    const guard = quizNavigationGuardRef.current;
    if (typeof guard === "function") return guard(action);
    action?.();
    return true;
  }, []);
  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));
  const select = (key) => {
    if (selected === key) return;
    runWithQuizGuard(() => {
      setSelected(key);
      setExpanded((e) => ({ ...e, [parentOf(key)]: true }));
    });
  };

  // Switch modules → reset the in-module node selection.
  const selectModule = (id) => {
    if (selectedModuleId === id) return;
    runWithQuizGuard(() => {
      setSelectedModuleId(id);
      setSelected("intro");
      setExpanded({ intro: true });
    });
  };

  const run = async (fn, { refresh = true } = {}) => {
    try {
      globalLoader(true);
      const res = await fn();
      globalLoader(false);
      if (res?.data?.status === 200) {
        if (refresh) onRefresh?.();
        return res;
      }
      globalAlert("error", res?.data?.message);
    } catch (e) {
      globalLoader(false);
      if (isEndpointMissing(e))
        globalAlert("warning", t("admin.lms.module.duplicateComingSoon"));
      else globalAlert("error", e?.response?.data?.message);
    }
    return null;
  };

  const reorderVia = (updateFn) => async (ordered) => {
    try {
      globalLoader(true);
      for (let i = 0; i < ordered.length; i++) {
        const a = ordered[i];
        if (a.order !== i + 1)
          await updateFn(assetIdOf(a), { ...assetPayload(a), order: i + 1 });
      }
      globalLoader(false);
      onRefresh?.();
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
    }
  };

  /* ── module management ── */
  const moveModule = (i, dir) => {
    const tgt = i + dir;
    if (tgt < 0 || tgt >= sortedModules.length) return;
    const next = [...sortedModules];
    [next[i], next[tgt]] = [next[tgt], next[i]];
    run(() => lmsReorderModulesApi(courseId, next.map(mid)));
  };
  const endModuleDrag = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };
  const onModuleDrop = (dropIndex) => {
    // dropIndex === sortedModules.length ⇒ dropped past the last row (append).
    if (dragIndex === null) return endModuleDrag();
    const clamped = Math.min(dropIndex, sortedModules.length - 1);
    if (dragIndex === clamped) return endModuleDrag();
    const next = [...sortedModules];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(clamped, 0, moved);
    endModuleDrag();
    run(() => lmsReorderModulesApi(courseId, next.map(mid)));
  };
  const deleteModule = (m) => {
    setToDeleteModule(null);

    if (mid(m) === selectedModuleId) {
      setSelectedModuleId(null);
    }

    run(async () => {
      const res = await lmsDeleteModuleApi(mid(m));

      if (res?.data?.status === 200) {
        setWizardDirty?.(true);
      }

      return res;
    });
  };
  const openAddModule = () => {
    setNewModuleTitle("");
    setAddModuleOpen(true);
  };
  const closeAddModule = () => {
    setAddModuleOpen(false);
    setNewModuleTitle("");
  };
  const createModuleFromModal = async () => {
    const title = newModuleTitle.trim();
    if (!title) {
      globalAlert("error", t("admin.lms.module.titleRequired"));
      return;
    }

    try {
      globalLoader(true);
      const res = await lmsSaveModuleApi({
        courseId,
        title,
        description: "",
      });

      if (res?.data?.status !== 200) {
        globalLoader(false);
        globalAlert("error", res?.data?.message);
        return;
      }

      const savedModule = res?.data?.data?.module || res?.data?.data || {};
      const savedModuleId = mid(savedModule);

      if (savedModuleId && !(savedModule?.sections || []).length) {
        const sectionRes = await lmsAddSectionApi(savedModuleId, {
          title: t("admin.lms.section.defaultTitle", { n: 1 }),
          order: 1,
        });
        if (sectionRes?.data?.status !== 200) {
          globalLoader(false);
          globalAlert("error", sectionRes?.data?.message);
          if (savedModuleId) selectModule(savedModuleId);
          onRefresh?.();
          closeAddModule();
          return;
        }
      }

      globalLoader(false);
      globalAlert("success", res?.data?.message);
      setWizardDirty?.(true);
      closeAddModule();
      if (savedModuleId) {
        selectModule(savedModuleId);
        setSelected("intro");
      }
      onRefresh?.();
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message);
    }
  };
  const openEditModuleTitle = () => {
    setModuleTitleDraft(module?.title || "");
    setEditTitleOpen(true);
  };
  const closeEditModuleTitle = () => {
    setEditTitleOpen(false);
    setModuleTitleDraft("");
  };
  const updateModuleTitle = async () => {
    const title = moduleTitleDraft.trim();
    if (!title) {
      globalAlert("error", t("admin.lms.module.titleRequired"));
      return;
    }

    try {
      globalLoader(true);
      const res = await lmsSaveModuleApi({
        courseId,
        moduleId,
        title,
      });

      globalLoader(false);
      if (res?.data?.status === 200) {
        globalAlert("success", res?.data?.message);
        closeEditModuleTitle();
        setWizardDirty?.(true);
        onRefresh?.();
        return;
      }

      globalAlert("error", res?.data?.message);
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message);
    }
  };

  /* ── section management ── */
  const addSection = async () => {
    runWithQuizGuard(async () => {
      const res = await run(
        () =>
          lmsAddSectionApi(moduleId, {
            title: t("admin.lms.section.defaultTitle", { n: sections.length + 1 }),
          }),
        { refresh: false }
      );
      if (res) {
        const sec = res?.data?.data?.section || res?.data?.data;
        onRefresh?.();
        if (sec) {
          setSelected(`sec-${sid(sec)}#video`);
          setExpanded((e) => ({ ...e, [`sec-${sid(sec)}`]: true }));
          setWizardDirty?.(true);
        }
      }
    });
  };
  const deleteSection = (sec) => {
    if (sections.length <= 1) {
      setConfirmSection(null);
      globalAlert(
        "warning",
        t(
          "admin.lms.section.minimumRequired",
          "A module must contain at least one Part."
        )
      );
      return;
    }
    setConfirmSection(null);
    setSelected("intro");
    run(async () => {
      const res = await lmsDeleteSectionApi(moduleId, sid(sec));

      if (res?.data?.status === 200) {
        setWizardDirty?.(true);
      }

      return res;
    });
  };

  /* ── build the element tree ── */
  // Sections are auto "Part N" (titles were removed in the authoring redesign);
  // ignore any legacy stored title so the label matches the learner sidebar.
  const partLabel = (s, i) => t("admin.lms.tree.part", "Part {{n}}", { n: i + 1 });

  const tree = useMemo(() => {
    if (!module) return [];
    let n = 1;
    const items = [];
    items.push({
      key: "intro",
      num: n++,
      label: t("admin.lms.tree.introduction", "Module Introduction"),
      icon: "fas fa-play-circle",
      ready: hasIntroVideo(module),
      children: intro.map((a) => ({
        label: a.title || a.type,
        icon: elIcon(a.type),
        ready: assetIsFilled(a),
      })),
    });
    sections.forEach((s, i) => {
      const sKey = `sec-${sid(s)}`;
      // Summary Slide is visible to authors but deliberately excluded from the
      // Part readiness calculation because it is optional.
      items.push({
        key: sKey,
        num: n++,
        label: partLabel(s, i),
        icon: "fas fa-layer-group",
        ready: sectionIsReady(s),
        children: [
          {
            key: `${sKey}#video`,
            label: t("admin.lms.tree.video", "Video"),
            icon: "fas fa-video",
            ready: sectionHasVideo(s),
          },
          {
            key: `${sKey}#summary`,
            label: t("admin.lms.tree.partSummary", "Summary Slide"),
            icon: "fas fa-file-pdf",
            ready: sectionHasSummary(s),
            optional: true,
          },
          {
            key: `${sKey}#quiz`,
            label: t("admin.lms.tree.quiz", "Quiz"),
            icon: "fas fa-clipboard-question",
            ready: sectionHasQuiz(s),
          },
        ],
      });
    });
    items.push({
      key: "summary",
      num: n++,
      label: t("admin.lms.tree.summary", "Key Tips"),
      icon: "fas fa-flag-checkered",
      ready: hasCompletionPdf(module),
      optional: true,
      children: completion.map((a) => ({
        label: a.title || a.type,
        icon: elIcon(a.type),
        ready: wrappedAssetIsFilled(a),
      })),
    });
    items.push({
      key: "support",
      num: n++,
      label: t("admin.lms.tree.support", "Local Support Services"),
      icon: "fas fa-hands-helping",
      ready: hasAnySupportPdf(module),
      optional: true,
      children: support.map((w) => ({
        label: `${t(`admin.lms.area.${w.serviceArea}`, w.serviceArea || "")} · ${w?.asset?.title || "PDF"}`,
        icon: elIcon(w?.asset?.type),
        ready: wrappedAssetIsFilled(w),
      })),
    });
    return items;
  }, [module, sections, intro, completion, support]);

  const selParent = parentOf(selected);
  const sectionView = selected.includes("#") ? selected.split("#")[1] : null;
  const activeSection = selParent.startsWith("sec-")
    ? sections.find((s) => `sec-${sid(s)}` === selParent)
    : null;
  const activeIndex = activeSection ? sections.indexOf(activeSection) : -1;
  const isQuizView = activeSection && sectionView === "quiz";
  const isSummaryView = activeSection && sectionView === "summary";

  const selectedNode = tree.find((i) => i.key === selected);
  const centerTitle = activeSection
    ? `${partLabel(activeSection, activeIndex)} - ${isQuizView
      ? t("admin.lms.tree.quiz", "Quiz")
      : isSummaryView
        ? t("admin.lms.tree.partSummary", "Summary Slide")
        : t("admin.lms.tree.video", "Video")
    }`
    : selected === "intro" && module?.title
      ? module.title
      : selectedNode?.label || "";
  const isModuleHeader = selected === "intro";
  const panelIcon = activeSection
    ? isQuizView
      ? "fas fa-question-circle"
      : isSummaryView
        ? "fas fa-file-pdf"
        : "fas fa-video"
    : selectedNode?.icon || "fas fa-layer-group";
  const panelSubtitle =
    activeSection
      ? isQuizView
        ? t(
          "admin.lms.tree.quizHint",
          "The questions learners must pass to complete this Part."
        )
        : isSummaryView
          ? t(
            "admin.lms.section.summaryPanelSubtitle",
            "Add an optional PDF summary for {{part}}. It is not required to publish or complete the course.",
            { part: partLabel(activeSection, activeIndex) }
          )
          : t(
            "admin.lms.section.videoPanelSubtitle",
            "Add and manage videos for {{part}}.",
            { part: partLabel(activeSection, activeIndex) }
          )
      : selected === "intro"
        ? ""
        : selected === "summary"
          ? t(
            "admin.lms.tree.summaryHint",
            "Key tips shown after all parts are complete (PDF, no voice-over)."
          )
          : selected === "support"
            ? t(
              "admin.lms.tree.supportHint",
              "Local support services shown after module completion — one PDF per area."
            )
            : "";
  const confirmDeleteSupportAsset = (wrapperId) => {
    run(async () => {
      const res = await lmsDeleteSupportAssetApi(moduleId, wrapperId);

      if (res?.data?.status === 200) {
        setWizardDirty?.(true);
      }

      return res;
    });
  };
  const deleteKeyTipsAsset = (asset) => {
    setToDeleteKeyTips({
      asset,
      kind: "keyTips",
    });

  };

  return (
    <div className={`lms-mtree ${noModules ? "is-empty" : ""}`}>
      {/* LEFT: module list + element tree */}
      <div className="lms-mtree-nav">
        {/* Module switcher */}
        <div className="lms-mtree-modhead">
          <div className="lms-mtree-modhead-main">
            <span className="lms-mtree-modhead-icon">
              <i className="fas fa-book-open"></i>
            </span>
            <div>
              <span className="lms-mtree-modhead-label">
                {t("admin.lms.course.modulesHeading")}
              </span>
            </div>
          </div>
          <button
            className="btn btn-md btn-add-module"
            onClick={openAddModule}
          >
            <i className="fas fa-plus"></i> {t("admin.lms.course.side.addModule")}
          </button>
        </div>

        <div
          className={`lms-mtree-modzone ${dragIndex !== null ? "is-dragging" : ""}`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => onModuleDrop(sortedModules.length)}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) setDragOverIndex(null);
          }}
        >
          {sortedModules.length === 0 && (
            <div className="lms-module-empty-side">
              {/* <div className="lms-module-empty-illustration">
                <span className="lms-module-empty-doc">
                  <i className="far fa-file-alt"></i>
                </span>
                <span className="lms-module-empty-plus">
                  <i className="fas fa-plus"></i>
                </span>
              </div> */}
              <h4>{t("admin.lms.module.noModulesTitle", "No modules yet")}</h4>
              <p>
                {t(
                  "admin.lms.module.noModulesBody",
                  "Modules help you organize your course content into structured sections."
                )}
              </p>
            </div>
          )}

          {sortedModules.map((m, i) => {
            const active = selectedModuleId === mid(m);
            const ready = moduleIsReady(m);
            return (
              <div
                key={mid(m) || i}
                className={`lms-mq-item lms-modnode ${active ? "active" : ""} ${dragIndex === i ? "is-dragging" : ""
                  } ${dragOverIndex === i && dragIndex !== i ? "is-over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (dragOverIndex !== i) setDragOverIndex(i);
                }}
                onDrop={(e) => {
                  e.stopPropagation();
                  onModuleDrop(i);
                }}
              >
                <div
                  className="lms-modnode-head"
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragEnd={endModuleDrag}
                  onClick={() => selectModule(mid(m))}
                >
                  <div className="lms-mq-item-main">
                    <i
                      className="fas fa-grip-vertical lms-drag-handle"
                      title={t("admin.lms.tree.dragHint", "Drag to reorder modules")}
                    ></i>
                    <span className="lms-mq-order">{i + 1}</span>
                    <div className="lms-modnode-content">
                      <span className="lms-modnode-title">{m?.title || "-"}</span>
                    </div>
                    <span
                      className={`lms-tree-status ${statusClass(ready)}`}
                      title={
                        ready
                          ? t("admin.lms.tree.ready", "Ready")
                          : t("admin.lms.tree.pending", "Pending")
                      }
                    >
                      <i
                        className={`fas ${ready ? "fa-check-circle" : "fa-exclamation-triangle"
                          }`}
                      ></i>
                    </span>
                  </div>
                  <div className="lms-modnode-tools" onClick={(e) => e.stopPropagation()}>
                    <div className="lms-mq-reorder">
                      <button
                        className="btn btn-xs"
                        disabled={i === 0}
                        onClick={() => moveModule(i, -1)}
                      >
                        <i className="fas fa-chevron-up"></i>
                      </button>
                      <button
                        className="btn btn-xs"
                        disabled={i === sortedModules.length - 1}
                        onClick={() => moveModule(i, 1)}
                      >
                        <i className="fas fa-chevron-down"></i>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Element sequence nested inside the selected module */}
                {active && (
                  <div
                    className="lms-mtree-modbody"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tree.map((it) => {
                      const hasChildren = (it.children || []).length > 0;
                      const open = !!expanded[it.key];
                      const showStatus = !it.optional || it.ready;
                      return (
                        <div className="lms-mtree-group" key={it.key}>
                          <button
                            className={`lms-mtree-item ${selected === it.key ||
                              selected.startsWith(`${it.key}#`)
                              ? "active"
                              : ""
                              }`}
                            onClick={() =>
                              select(
                                it.key.startsWith("sec-")
                                  ? `${it.key}#video`
                                  : it.key
                              )
                            }
                          >
                            <span className="lms-mtree-num">{it.num}</span>
                            <i className={`${it.icon} lms-mtree-icon`}></i>
                            <span className="lms-mtree-label">{it.label}</span>
                            {showStatus && (
                              <span
                                className={`lms-tree-status ${statusClass(it.ready)}`}
                                title={
                                  it.ready
                                    ? t("admin.lms.tree.ready", "Ready")
                                    : t("admin.lms.tree.pending", "Pending")
                                }
                              >
                                <i
                                  className={`fas ${it.ready
                                    ? "fa-check-circle"
                                    : "fa-exclamation-triangle"
                                    }`}
                                ></i>
                              </span>
                            )}
                            {hasChildren && (
                              <i
                                className={`fas fa-chevron-${open ? "up" : "down"
                                  } lms-mtree-caret`}
                                role="button"
                                tabIndex={-1}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle(it.key);
                                }}
                              ></i>
                            )}
                          </button>
                          {hasChildren && open && (
                            <div className="lms-mtree-children">
                              {it.children.map((c, ci) => (

                                <button
                                  key={c.key || ci}
                                  className={`lms-mtree-child ${c.key && selected === c.key ? "active" : ""
                                    }`}
                                  onClick={() => select(c.key || it.key)}
                                >
                                  <span className="lms-mtree-child-num">
                                    {it.num}.{ci + 1}
                                  </span>
                                  <i className={`${c.icon} lms-mtree-child-icon`}></i>
                                  <span className="lms-mtree-child-label">{c.label}</span>
                                  {c.optional && !c.ready && (
                                    <span className="lms-optional-chip">
                                      {t("common.optional", "Optional")}
                                    </span>
                                  )}
                                  {(!c.optional || c.ready) && (
                                    <span
                                      className={`lms-tree-status ${statusClass(c.ready)}`}
                                      title={
                                        c.ready
                                          ? t("admin.lms.tree.ready", "Ready")
                                          : t("admin.lms.tree.pending", "Pending")
                                      }
                                    >
                                      <i
                                        className={`fas ${c.ready
                                          ? "fa-check-circle"
                                          : "fa-exclamation-triangle"
                                          }`}
                                      ></i>
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <button
                      className="btn secondary-btn btn-with-icon lms-mtree-add"
                      onClick={addSection}
                    >
                      <i className="fas fa-plus"></i>{" "}
                      {t("admin.lms.tree.addPart", "Add Part")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {sortedModules.length > 1 && (
            <p className="lms-mtree-modzone-hint">
              <i className="fas fa-arrows-alt-v"></i>{" "}
              {t("admin.lms.tree.dragHint", "Drag to reorder modules")}
            </p>
          )}
        </div>
      </div>

      {/* RIGHT: contextual editor */}
      <div
        className={`lms-mtree-panel ${module && selected === "intro" ? "is-module-introduction" : ""
          }`}
      >
        {module && (
          <>
            <div
              className={`lms-mtree-panel-head ${isModuleHeader ? "is-module-header" : ""
                }`}
            >
              <div className="lms-mtree-panel-heading">
                <span className="lms-mtree-panel-icon">
                  <i className={panelIcon}></i>
                </span>
                <div>
                  <div className="lms-mtree-panel-title-row">
                    <h4 className="lms-mtree-panel-title">{centerTitle}</h4>
                    {isModuleHeader && (
                      <button
                        type="button"
                        className="lms-panel-title-edit-btn"
                        onClick={openEditModuleTitle}
                        aria-label={t(
                          "admin.lms.module.editTitleButton",
                          "Edit title"
                        )}
                        title={t(
                          "admin.lms.module.editTitleButton",
                          "Edit title"
                        )}
                      >
                        <i className="fas fa-pen"></i>
                      </button>
                    )}
                    {!isModuleHeader && activeSection && (
                      <button
                        type="button"
                        className="lms-panel-title-edit-btn lms-panel-title-delete-btn"
                        onClick={() => setConfirmSection(activeSection)}
                        aria-label={t("admin.lms.section.deletePart", "Delete Part")}
                        title={t("admin.lms.section.deletePart", "Delete Part")}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </div>
                  {panelSubtitle && (
                    <p className="lms-mtree-panel-subtitle">{panelSubtitle}</p>
                  )}
                </div>
              </div>

              {isModuleHeader && (
                <div className="lms-module-header-actions">
                  <button
                    type="button"
                    className="lms-module-header-action is-danger"
                    onClick={() => setToDeleteModule(module)}
                  >
                    <i className="fas fa-trash"></i>
                    {t("common.delete", "Delete")}
                  </button>
                </div>
              )}

              {!isModuleHeader && activeSection && isQuizView && (
                <div className="lms-mtree-toolbar">
                  {/* <button
                    className="btn btn-xs"
                    disabled={activeIndex === 0}
                    onClick={() => moveSection(activeIndex, -1)}
                    title={t("admin.lms.quiz.moveUp")}
                  >
                    <i className="fas fa-arrow-up"></i>
                  </button> */}
                  {/* <button
                    className="btn btn-xs"
                    disabled={activeIndex === sections.length - 1}
                    onClick={() => moveSection(activeIndex, 1)}
                    title={t("admin.lms.quiz.moveDown")}
                  >
                    <i className="fas fa-arrow-down"></i>
                  </button> */}
                  {/* {courseId && (
                    <a
                      className="btn btn-xs"
                      href={`/admin/lms/course/${courseId}/preview?module=${moduleId}&section=${sid(
                        activeSection
                      )}`}
                      title={t("admin.lms.preview.preview")}
                    >
                      <i className="fas fa-eye"></i>
                    </a>
                  )} */}
                </div>
              )}
            </div>

            {selected === "intro" && (
              <div className="lms-module-introduction-panel">
                <LmsAssetList
                  variant="moduleIntro"
                  assets={intro}
                  addLabel={t("admin.lms.module.addModuleVideoIntro")}
                  uploadTitle={moduleIntroUploadTitle(course, module?.title)}
                  uploadContext={{
                    ...moduleUploadContext,
                    assetRole: "module-intro",
                  }}
                  onAdd={(a) =>
                    run(async () => {
                      const res = await lmsAddModuleIntroAssetApi(moduleId, assetPayload(a));

                      if (res?.data?.status === 200) {
                        setWizardDirty?.(true);
                      }

                      return res;
                    })
                  }
                  onDelete={(a) => setToDeleteAsset(a)}
                  onReorder={reorderVia((id, body) =>
                    lmsUpdateModuleIntroAssetApi(moduleId, id, body)
                  )}
                  maxItems={1}
                  accept="video/*"
                />
              </div>
            )}
            {activeSection && (
              <LmsSectionEditor
                moduleId={moduleId}
                moduleTitle={module?.title}
                section={activeSection}
                onRefresh={onRefresh}
                showFlowHint={false}
                part={sectionView || "video"}
                uploadTitle={partLabel(activeSection, activeIndex)}
                uploadContext={moduleUploadContext}
                registerUnsavedChangesGuard={(guard) => {
                  quizNavigationGuardRef.current = guard;
                  registerUnsavedChangesGuard?.(guard);
                }}
                setWizardDirty={setWizardDirty}
              />

            )}

            {selected === "summary" && (
              <>
                <LmsAssetList
                  variant="keyTips"
                  assetKind="pdf"
                  assets={completion}
                  accept="application/pdf"
                  maxItems={1}
                  addLabel={t("admin.lms.tree.addKeyTips", "Upload Key Tips PDF")}
                  uploadTitle={`${module?.title || "Module"} Key Tips`}
                  uploadContext={{
                    ...moduleUploadContext,
                    assetRole: "key-tips",
                  }}
                  helperText={t(
                    "admin.lms.keyTips.uploadHint",
                    "Upload a PDF with the key tips learners should keep after finishing this module."
                  )}
                  emptyTitle={t(
                    "admin.lms.keyTips.emptyTitle",
                    "No Key Tips PDF uploaded yet"
                  )}
                  emptyBody={t(
                    "admin.lms.keyTips.emptyBody",
                    "Upload the Key Tips PDF learners will see after completing this module."
                  )}
                  recommendedText={t(
                    "admin.lms.keyTips.recommended",
                    "Recommended: PDF (Max. 50MB)"
                  )}
                  onAdd={(a) =>
                    run(async () => {
                      const res = await lmsAddCompletionAssetApi(
                        moduleId,
                        assetPayload(a)
                      );

                      if (res?.data?.status === 200) {
                        setWizardDirty?.(true);
                      }

                      return res;
                    })
                  }
                  onDelete={deleteKeyTipsAsset}
                  onReorder={reorderVia(async (id, body) => {
                    const res = await lmsUpdateCompletionAssetApi(moduleId, id, body);

                    if (res?.data?.status === 200) {
                      setWizardDirty?.(true);
                    }

                    return res;
                  })}
                />
              </>
            )}

            {selected === "support" && (
              <>
                <LmsAudienceAssets
                  variant="localSupport"
                  values={SERVICE_AREAS}
                  items={support}
                  wrapKey="serviceArea"
                  accept="application/pdf"
                  labelFor={(v) => `${t(`admin.lms.area.${v}`, v)}`}
                  uploadContext={(area) => ({
                    ...moduleUploadContext,
                    assetRole: "local-support",
                    serviceArea: area,
                  })}
                  onAdd={(area, a) =>
                    run(async () => {
                      const res = await lmsAddSupportAssetApi(moduleId, {
                        serviceArea: area,
                        asset: assetPayload(a),
                      });

                      if (res?.data?.status === 200) {
                        setWizardDirty?.(true);
                      }

                      return res;
                    })
                  }
                  onRemove={(wrapperId) =>
                    setToDeleteSupportAsset(wrapperId)
                  }
                />
              </>
            )}
          </>
        )}

        {!module && (
          <div className="lms-module-empty-stage">
            <div className="lms-module-empty-stage-inner">
              <span className="lms-module-empty-stage-icon">
                <i className="fas fa-book-open"></i>
              </span>
              <h3>
                {t(
                  "admin.lms.module.addFirstTitle",
                  "Add a new module to get started"
                )}
              </h3>
              <p>
                {t(
                  "admin.lms.module.addFirstBody",
                  "Modules are the building blocks of your course. You can add lessons, videos, documents, quizzes and more inside a module."
                )}
              </p>
              <button
                type="button"
                className="btn secondary-btn btn-with-icon lms-module-empty-stage-btn"
                onClick={openAddModule}
              >
                <i className="fas fa-plus"></i>
                {t("admin.lms.course.addModule")}
              </button>
              {/* <span className="lms-module-empty-stage-hint">
                {t(
                  "admin.lms.module.addFirstHint",
                  "Click the button to create your first module"
                )}
              </span> */}
            </div>
          </div>
        )}
      </div>

      {addModuleOpen && (
        <div
          className="modal fade show lms-add-module-modal"
          style={{ display: "block", backgroundColor: "rgba(9, 18, 43, 0.55)" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <form
              className="modal-content"
              onSubmit={(e) => {
                e.preventDefault();
                createModuleFromModal();
              }}
            >
              <div className="modal-header d-flex justify-content-between align-items-start">
                <div className="lms-add-module-title-wrap d-flex align-items-start">
                  <span className="lms-add-module-icon">
                    <i className="fas fa-book-open"></i>
                  </span>

                  <div>
                    <h3>{t("admin.lms.module.addModuleTitle", "Add module")}</h3>
                    <p>
                      {t(
                        "admin.lms.module.addModuleSubtitle",
                        "Name the module. You can add parts and content after it is created."
                      )}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn lms-modal-close ml-auto"
                  aria-label={t("common.close", "Close")}
                  onClick={closeAddModule}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <label className="form-own-label lms-add-module-label">
                  {t("admin.lms.module.moduleName", "Module Name")}
                  <span className="text-danger ml-1">*</span>
                </label>
                <input
                  className="form-control lms-add-module-input"
                  value={newModuleTitle}
                  maxLength={150}
                  placeholder={t(
                    "admin.lms.module.namePlaceholder",
                    "Enter module name"
                  )}
                  onChange={(e) => setNewModuleTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") closeAddModule();
                  }}
                  autoFocus
                />
                <span className="lms-add-module-count">
                  {newModuleTitle.length}/150
                </span>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-own-grey"
                  onClick={closeAddModule}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button type="submit" className="btn secondary-btn btn-with-icon">
                  <i className="fas fa-check"></i>
                  {t("common.ok", "OK")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editTitleOpen && (
        <div
          className="modal fade show lms-add-module-modal lms-edit-module-title-modal"
          style={{ display: "block", backgroundColor: "rgba(9, 18, 43, 0.55)" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <form
              className="modal-content"
              onSubmit={(e) => {
                e.preventDefault();
                updateModuleTitle();
              }}
            >
              <div className="modal-header">
                <div className="lms-add-module-title-wrap">
                  <span className="lms-add-module-icon">
                    <i className="fas fa-pen"></i>
                  </span>
                  <div>
                    <h3>
                      {t("admin.lms.module.editModuleTitle", "Edit Module Name")}
                    </h3>
                    <p>
                      {t(
                        "admin.lms.module.editModuleTitleSubtitle",
                        "Update the name shown in the module list and course contents."
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn lms-modal-close"
                  aria-label={t("common.close", "Close")}
                  onClick={closeEditModuleTitle}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <label className="form-own-label lms-add-module-label">
                  {t("admin.lms.module.moduleTitle")}
                  <span className="text-danger ml-1">*</span>
                </label>
                <input
                  className="form-control lms-add-module-input"
                  value={moduleTitleDraft}
                  maxLength={150}
                  placeholder={t(
                    "admin.lms.module.titlePlaceholder",
                    "Enter module title..."
                  )}
                  onChange={(e) => setModuleTitleDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") closeEditModuleTitle();
                  }}
                  autoFocus
                />
                <span className="lms-add-module-count">
                  {moduleTitleDraft.length}/150
                </span>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-own-grey"
                  onClick={closeEditModuleTitle}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button type="submit" className="btn secondary-btn btn-with-icon">
                  <i className="fas fa-check"></i>
                  {t("admin.lms.module.updateTitle", "Update title")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmSection && (
        <ConfirmationModal
          open={!!confirmSection}
          deleteInfo={[partLabel(confirmSection, sections.indexOf(confirmSection))]}
          description={t("admin.lms.section.confirmDeletePart", "Delete this Part and its quiz?")}
          onCloseModal={(ok) =>
            ok ? deleteSection(confirmSection) : setConfirmSection(null)
          }
        />
      )}

      {toDeleteModule && (
        <ConfirmationModal
          open={!!toDeleteModule}
          deleteInfo={[
            `${t("admin.lms.module.moduleTitle")} : ${toDeleteModule?.title}`,
          ]}
          description={t("admin.lms.course.confirmDeleteModule")}
          onCloseModal={(ok) =>
            ok ? deleteModule(toDeleteModule) : setToDeleteModule(null)
          }
        />
      )}
      {toDeleteAsset && (
        <ConfirmationModal
          open={!!toDeleteAsset}
          deleteInfo={[
            `${t("admin.lms.asset.videoTitle", "Video")} : ${toDeleteAsset.title || toDeleteAsset.filename
            }`,
          ]}
          description={t(
            "admin.lms.asset.confirmDeleteVideo",
            "Are you sure you want to delete this video?"
          )}
          onCloseModal={(ok) => {
            if (ok) {
              run(async () => {
                const res = await lmsDeleteModuleIntroAssetApi(
                  moduleId,
                  assetIdOf(toDeleteAsset)
                );

                if (res?.data?.status === 200) {
                  setWizardDirty?.(true);
                }

                return res;
              });
            }

            setToDeleteAsset(null);
          }}
        />
      )}
      {toDeleteKeyTips && (
        <ConfirmationModal
          open={!!toDeleteKeyTips}
          deleteInfo={[
            `Key Tips : ${toDeleteKeyTips.asset.title ||
            toDeleteKeyTips.asset.filename ||
            "-"
            }`,
          ]}
          description={t(
            "admin.lms.asset.confirmDeleteCompletion",
            "Are you sure you want to delete this Key Tips?"
          )}
          onCloseModal={(ok) => {
            if (ok) {
              run(async () => {
                const res = await lmsDeleteCompletionAssetApi(
                  moduleId,
                  assetIdOf(toDeleteKeyTips.asset)
                );

                if (res?.data?.status === 200) {
                  setWizardDirty?.(true);
                }

                return res;
              });
            }

            setToDeleteKeyTips(null);
          }}
        />
      )}
      {toDeleteSupportAsset && (
        <ConfirmationModal
          open={!!toDeleteSupportAsset}
          deleteInfo={[
            `${t(
              "admin.lms.support.serviceArea",
              "Local Support Service"
            )}`,
          ]}
          description={t(
            "admin.lms.support.confirmDeleteAsset",
            "Are you sure you want to delete this Local Support Service ?"
          )}
          onCloseModal={(ok) => {
            if (ok) {
              confirmDeleteSupportAsset(toDeleteSupportAsset);
            }
            setToDeleteSupportAsset(null);
          }}
        />
      )}
    </div>
  );
};

export default LmsModuleWorkspace;
